import { useState, useEffect, useMemo } from "react";
import { Barcode, Eye } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import LabelsLayout from "../componants/labels/LabelsLayout";
import LabelBreadcrumb from "../componants/labels/LabelBreadcrumb";
import { useCollection } from "../hooks/useCollection";
import EvigilanceLabel from "../componants/labels/EvigilanceLabel";

export default function CertificatEvigilance() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const { state } = useLocation();

  const { documents, loading, error } = useCollection("certificates");
  // 1) On filtre correctement les docs (find ➔ filter)
  const docs = useMemo(
    () => documents.filter((d) => d.types === "e-Vigilance CEISNA"),
    [documents]
  );

  const [filteredDocs, setFilteredDocs] = useState([]);

  // 3) À chaque changement de docs, on réinitialise filteredDocs
  useEffect(() => {
    setFilteredDocs(docs);
    setCurrentPage(1);
  }, [docs]);

  const totalItems = filteredDocs.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // 4) Pagination basée sur filteredDocs
  const paginatedData = useMemo(
    () =>
      filteredDocs.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      ),
    [filteredDocs, currentPage, itemsPerPage]
  );

  const [isOpen, setIsOpen] = useState(false);

  const [formData, setFormData] = useState({
    organisme: "",
    entityId: "",
    entityName: "",
    version: "",
    reference: "",
    types: "",
    startDate: "",
    endDate: "",
    status: "",
    isHistory: false,
  });

  const handleChange = (key, value) =>
    setFormData((prev) => ({ ...prev, [key]: value }));
  const toggleOpen = () => setIsOpen((o) => !o);

  // utilitaire pour afficher un skeleton row
  const renderSkeletonRow = (_, idx) => (
    <tr key={idx} className={idx % 2 ? "bg-gray-50" : "bg-white"}>
      {Array.from({ length: 10 }).map((__, i) => (
        <td key={i} className="px-4 py-2">
          <div className="h-4 bg-gray-200 rounded animate-pulse" />
        </td>
      ))}
    </tr>
  );

  // 2) Réinitialisez filteredDocs dès que docs change
  useEffect(() => {
    setFilteredDocs(docs);
    setCurrentPage(1);
  }, [docs]);

  // 3) filterHandler : n’applique chaque critère de formData que si sa valeur est non vide
  const filterHandler = (e) => {
    e.preventDefault();

    const results = docs.filter((doc) => {
      return Object.entries(formData).every(([key, val]) => {
        // ignore empty strings, null/undefined, and false for isHistory
        if (val === "" || val == null || (key === "isHistory" && !val)) {
          return true;
        }

        // special case for the boolean flag
        if (key === "isHistory") {
          return doc.isHistory === true;
        }

        // normalize the search term: if it's an object (e.g. React-Select), pick name or value
        const term = typeof val === "object" ? val.name ?? "" : val;

        if (!term) {
          return true;
        }

        const fieldValue = doc[key];

        // string fields: case-insensitive substring match
        if (typeof fieldValue === "string") {
          return fieldValue.toLowerCase().includes(term.toLowerCase());
        }

        // for numbers or exact matches
        return fieldValue === term;
      });
    });

    setIsOpen(false);
    setFilteredDocs(results);
    setCurrentPage(1);
  };

  useEffect(() => {
    // Ne rien faire tant que docs n'est pas chargé ou que state est absent
    if (!state || docs.length === 0) return;

    const results = docs.filter((doc) => {
      return Object.entries(formData).every(([key, val]) => {
        // ignore empty strings, null/undefined, and false for isHistory
        if (val === "" || val == null || (key === "isHistory" && !val)) {
          return true;
        }

        // special case for the boolean flag
        if (key === "isHistory") {
          return doc.isHistory === true;
        }

        // normalize the search term: if it's an object (e.g. React-Select), pick name or value
        const term = typeof val === "object" ? val.name ?? "" : val;

        if (!term) {
          return true;
        }

        const fieldValue = doc[key];

        // string fields: case-insensitive substring match
        if (typeof fieldValue === "string") {
          return fieldValue.toLowerCase().includes(term.toLowerCase());
        }

        // for numbers or exact matches
        return fieldValue === term;
      });
    });

    setIsOpen(false);
    setFilteredDocs(results);
    setCurrentPage(1);
    setFormData((prev) => ({
      ...prev,
      ...state,
    }));
  }, [state, docs]);

  const intialisationHandler = () => {
    setFormData({
      organisme: "",
      entityId: "",
      entityName: "",
      version: "",
      reference: "",
      types: "",
      startDate: "",
      endDate: "",
      status: "",
      isHistory: false,
    });
  };

  return (
    <LabelsLayout>
      <section className="container mx-auto">
        <LabelBreadcrumb title="e-Vigilance CEISNA" />
        <div className="pb-10 pt-5 px-6 flex flex-col">
          <h1 className="md:text-2xl text-[1rem] font-semibold mb-4 flex items-center space-x-2">
            <Barcode className="md:w-6 w-4 h-4 md:h-6 text-gray-600" />
            <span>e-Vigilance CEISNA</span>
          </h1>

          {/* Recherche */}
          <EvigilanceLabel
            handleChange={handleChange}
            formData={formData}
            isOpen={isOpen}
            toggleOpen={toggleOpen}
            searchHandler={filterHandler}
            intialisationHandler={intialisationHandler}
          />
          <div className="mb-4 flex items-center text-sm">
            <span className="mr-2 text-gray-700">Recherche pour :</span>
            <button
              onClick={() => setIsOpen(true)}
              className="text-blue-900 underline"
            >
              Nouvelle recherche
            </button>
          </div>

          {/* Tableau */}
          <div className="overflow-auto md:w-full   w-[86vw] bg-white rounded shadow">
            <table className="min-w-full table-auto">
              <thead className="bg-blue-900 text-white">
                <tr>
                  {[
                    "Organisme",
                    "ID entité",
                    "Nom entité",
                    "Version",
                    "Référence",
                    "Type",
                    "Début validité",
                    "Fin validité",
                    "Statut",
                    "Actions",
                  ].map((col) => (
                    <th
                      key={col}
                      className="px-4 py-2 text-left text-sm font-semibold"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* 1) pendant le chargement */}
                {loading &&
                  Array.from({ length: 5 }).map((_, idx) =>
                    renderSkeletonRow(_, idx)
                  )}

                {/* 2) si erreur */}
                {!loading && error && (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-4 py-6 text-center text-red-600"
                    >
                      Erreur lors du chargement : {error}
                    </td>
                  </tr>
                )}

                {/* 3) si pas de données */}
                {!loading && !error && totalItems === 0 && (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-4 py-6 text-center text-gray-600"
                    >
                      Aucun document trouvé.
                    </td>
                  </tr>
                )}

                {/* 4) les vraies données */}
                {!loading &&
                  !error &&
                  paginatedData.map((row, idx) => (
                    <tr
                      key={row.id || idx}
                      className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    >
                      <td className="px-4 py-2 text-sm text-gray-800">
                        {row.organisme}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-800">
                        {row.entityId}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-800">
                        {row.entityName}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-800">
                        {row.version}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-800">
                        {row.reference}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-800">
                        {row.types}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-800">
                        {row.startDate}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-800">
                        {row.endDate}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-800">
                        {row.status}
                      </td>
                      <td className="px-4 py-2 flex space-x-2 items-center">
                        <button
                          onClick={() =>
                            navigate(`/certificats/e-vigilance/${row.id}`)
                          }
                          className="hover:text-blue-600"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {paginatedData.length > 0 && (
            <div className="mt-4 flex items-center justify-between text-sm">
              <div className=" flex flex-col  md:flex-row">
                <span className="  text-[0.8rem] md:text-[1rem]">
                  Éléments par page :&nbsp;
                </span>
                <select
                  className="border-gray-300 border rounded px-2 py-1"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(+e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                {(currentPage - 1) * itemsPerPage + 1}–
                {Math.min(currentPage * itemsPerPage, totalItems)} sur{" "}
                {totalItems}
              </div>
              <div className="flex flex-col md:flex-row items-center space-x-2">
                <div className=" flex flex-row">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                    className="px-2 text-[0.7rem] md:text-[1rem] py-1 rounded disabled:opacity-50"
                  >
                    Première
                  </button>
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((c) => Math.max(1, c - 1))}
                    className="px-2 py-1 text-[0.7rem] md:text-[1rem] rounded disabled:opacity-50"
                  >
                    ‹ Précédent
                  </button>
                </div>
                <span>Page</span>
                <select
                  className="border-gray-300 border rounded px-2 py-1"
                  value={currentPage}
                  onChange={(e) => setCurrentPage(+e.target.value)}
                >
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    )
                  )}
                </select>
                <div className=" flex flex-row">
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() =>
                      setCurrentPage((c) => Math.min(totalPages, c + 1))
                    }
                    className="px-2 py-1 text-[0.7rem] md:text-[1rem] rounded disabled:opacity-50"
                  >
                    Suivant ›
                  </button>
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                    className="px-2 text-[0.7rem] md:text-[1rem] py-1 rounded disabled:opacity-50"
                  >
                    Dernière
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </LabelsLayout>
  );
}
